import { Fragment, useState } from "react"
import { useTranslation } from "react-i18next"
import { gql, useQuery } from "@apollo/client"
import { Flash, WARNING } from "../../util/Flash"
import "../../../style/index.css"
import tableStyle from "../../../style/table.module.css"
import buttonStyle from "../../../style/button.module.css"
import dropDownStyle from "../../util/input/DropDownFilter.module.css"
import { TableHeader } from "../../util/table/TableHeader"
import { TableContentLoader } from "../../util/table/TableContentLoader"
import { Pagination } from "../../util/table/Pagination"
import { Link } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes } from "@fortawesome/free-solid-svg-icons"
import cn from "classnames"
import { Banner } from "../../util/Banner"
import {
    getSafeFormatter,
    getTimeFormatter,
    getIntegerFormatter,
} from "../../../i18n"
import { DeletionWithAffectedModal } from "../../util/modals/DeletionWithAffectedModal"
import c from "capitalize"
import { SmartFilter } from "../../util/input/SmartFilter"
import { prisoBasePath, prisoBannerKey } from "./prisoUtils"

const PRISO_LIST_QUERY = gql`
    query prisoListsList(
        $pageNum: Int
        $order: [PrisoListAllOrder]
        $filter: PrisoListAllFilter
    ) {
        lists: prisoLists(
            limit: 20
            pageNum: $pageNum
            orderBy: $order
            filterParams: $filter
        ) {
            currentPage
            hasNext
            hasPrevious
            pages
            page {
                id
                name
                createdAt
                updatedAt
                productAmount
            }
        }
    }
`

const PRISO_FILTER_QUERY = gql`
    query getFilter {
        lists: prisoLists(limit: 0) {
            filterFields {
                name
                dataType
                displayName
                options
            }
        }
    }
`

const DELETE_PRISO_LIST = gql`
    mutation ($id: UUID!, $ignoreAffected: Boolean) {
        delete: deletePrisoList(id: $id, ignoreAffected: $ignoreAffected) {
            success
            affected {
                model
                name
                id
            }
            blockers {
                model
                name
                id
            }
        }
    }
`

export const PrisoListList = ({ prisoType }) => {
    const [pageNum, setPage] = useState(1)
    const [order, setOrder] = useState([])
    const [query, setQuery] = useState("")
    const [filter, setFilter] = useState(null)

    const { t, i18n } = useTranslation(["common", "table", "page_names"])

    const basePath = prisoBasePath(prisoType)
    const bannerKey = prisoBannerKey(prisoType)

    const { data, loading } = useQuery(PRISO_LIST_QUERY, {
        variables: {
            pageNum,
            order,
            query,
            filter: {
                ...filter,
                prisoTypeEquals: prisoType,
            },
        },
    })

    const [currentElement, setCurrentElement] = useState(null)

    const dateFormat = getSafeFormatter(getTimeFormatter(i18n.language))
    const intFormat = getSafeFormatter(getIntegerFormatter(i18n.language))

    return (
        <div>
            <Banner text={t(`page_names:${bannerKey}`)} />

            {currentElement && (
                <DeletionWithAffectedModal
                    id={currentElement?.id}
                    delete_mutation={DELETE_PRISO_LIST}
                    onDismiss={() => setCurrentElement(null)}
                    refetchQueries={[PRISO_LIST_QUERY]}
                    onCancel={() => setCurrentElement(null)}
                    name={currentElement?.name}
                />
            )}

            <div className={dropDownStyle.container}>
                <SmartFilter
                    filterFieldQuery={PRISO_FILTER_QUERY}
                    filterPath={(q) => q?.lists?.filterFields}
                    setQuery={setQuery}
                    setFilter={setFilter}
                    suggestedQuery={data?.lists?.suggestedQuery}
                    hasSearch={false}
                />
                <div className={tableStyle.centerButtonBar}>
                    <div className={tableStyle.buttonBar}>
                        <Link
                            to={`${basePath}/csv`}
                            className={buttonStyle.orangeButton}
                            style={{ width: "100%" }}
                        >
                            {c(t("table:add_new_priso_list"))}
                        </Link>
                    </div>
                </div>
            </div>

            {!loading && !data?.lists?.page?.length ? (
                <Flash text={t("table:no_results")} type={WARNING} />
            ) : null}
            <div
                className={`${tableStyle.table} ${tableStyle["table-cols-5"]}`}
                style={{
                    gridTemplateColumns: "repeat(4, 2fr) 0.5fr",
                }}
            >
                <TableHeader
                    className={tableStyle.header}
                    label={t("table:name")}
                    asc="NameAsc"
                    desc="NameDesc"
                    order={order}
                    setOrder={setOrder}
                />
                <TableHeader
                    className={tableStyle.header}
                    label={t("table:product_amount")}
                    asc="ProductAmountAsc"
                    desc="ProductAmountDesc"
                    order={order}
                    setOrder={setOrder}
                />
                <TableHeader
                    className={tableStyle.header}
                    label={t("table:created_at")}
                    asc="CreatedAtAsc"
                    desc="CreatedAtDesc"
                    order={order}
                    setOrder={setOrder}
                />
                <TableHeader
                    className={tableStyle.header}
                    label={t("table:updated_at")}
                    asc="UpdatedAtAsc"
                    desc="UpdatedAtDesc"
                    order={order}
                    setOrder={setOrder}
                />
                <p className={tableStyle.header} />
                {loading && !data?.lists && (
                    <TableContentLoader columns={5} />
                )}
                {data?.lists?.page?.map((s, i) => (
                    <Fragment key={i}>
                        <Link
                            className={cn(tableStyle.linkCell, tableStyle.cell)}
                            to={`${basePath}/${s.id}`}
                        >
                            {s.name}
                        </Link>
                        <p className={tableStyle.cell}>
                            {intFormat.format(s?.productAmount)}
                        </p>
                        <p className={tableStyle.cell}>
                            {dateFormat.format(new Date(s.createdAt))}
                        </p>
                        <p className={tableStyle.cell}>
                            {dateFormat.format(new Date(s.updatedAt))}
                        </p>
                        <p className={tableStyle.actionCell}>
                            <button
                                className={cn(
                                    tableStyle.actionButton,
                                    tableStyle.negativeActionButton,
                                )}
                                onClick={() => {
                                    setCurrentElement({
                                        id: s.id,
                                        name: s.name,
                                    })
                                }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </p>
                    </Fragment>
                ))}
            </div>
            <Pagination
                onPageChange={(page) => setPage(page)}
                pagination={data?.lists}
                loading={loading && !data?.lists}
            />
        </div>
    )
}
